// Users tab — list every account, edit email + force-TOTP + delete,
// and create new users. Server enforces admin gating; this just
// surfaces the available actions.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { confirm } from "../../components/confirm";
import { api, type AdminUser } from "../../lib/rpc";

interface UsersTabProps {
  notify: (msg: string) => void;
  /** Current admin's own user id — used to disable the "delete" button
   *  on their own row so they can't lock themselves out. */
  meId: string | null;
}

export function AdminUsersTab({ notify, meId }: UsersTabProps) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  // New-user form state.
  const [draftId, setDraftId] = useState("");
  const [draftPw, setDraftPw] = useState("");
  const [draftEmail, setDraftEmail] = useState("");
  const [draftAdmin, setDraftAdmin] = useState(false);
  const [creating, setCreating] = useState(false);
  // Per-row email edit buffer — keyed by user id so saving one row
  // doesn't lose another row's mid-edit value.
  const [emailDrafts, setEmailDrafts] = useState<Record<string, string>>({});

  const refresh = async () => {
    try { setUsers(await api.adminListUsers()); }
    catch (e: any) { notify(e?.message || "Couldn't load users"); setUsers([]); }
  };
  useEffect(() => { refresh(); }, []);

  const editEmail = (id: string, value: string) => {
    setEmailDrafts((prev) => ({ ...prev, [id]: value }));
  };
  const saveEmail = async (u: AdminUser) => {
    const next = (emailDrafts[u.id] ?? u.email ?? "").trim();
    if (next === (u.email || "")) return;
    try {
      await api.adminSetUserEmail(u.id, next);
      notify("Email updated");
      setEmailDrafts((prev) => {
        const { [u.id]: _drop, ...rest } = prev;
        return rest;
      });
      await refresh();
    } catch (e: any) {
      notify(e?.message || "Couldn't update email");
    }
  };

  const toggleForceTotp = async (u: AdminUser) => {
    try {
      await api.adminSetForceTotp(u.id, !u.forceTotp);
      notify(u.forceTotp ? "Force-TOTP off" : "Force-TOTP on");
      await refresh();
    } catch (e: any) {
      notify(e?.message || "Couldn't update");
    }
  };

  const remove = async (u: AdminUser) => {
    const ok = await confirm({
      title: `Delete ${u.id}?`,
      message: `Removes the account and any data owned by them. Audit log retains the actor's id. This can't be undone.`,
      confirmLabel: "Delete user",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.adminDeleteUser(u.id);
      notify(`Deleted ${u.id}`);
      await refresh();
    } catch (e: any) {
      notify(e?.message || "Delete failed");
    }
  };

  const create = async () => {
    if (creating) return;
    const id = draftId.trim();
    if (!id || draftPw.length < 6) {
      notify("Username + 6-char password required");
      return;
    }
    setCreating(true);
    try {
      // Email goes in the same call now — the server applies it BEFORE
      // attempting Auth0 provisioning so the OIDC callback can find the
      // user immediately. Status of the Auth0 step comes back in the
      // response so we can surface it to the admin.
      const result = await api.adminCreateUser({
        id, password: draftPw, isAdmin: draftAdmin,
        email: draftEmail.trim() || undefined,
      });
      let suffix = "";
      if (result.auth0?.attempted) {
        if (result.auth0.resetEmailSent) {
          suffix = result.auth0.created
            ? ` · Auth0 user created, reset email sent to ${result.email}`
            : ` · Auth0 user exists, fresh reset email sent to ${result.email}`;
        } else if (result.auth0.error) {
          suffix = ` · Auth0 failed: ${result.auth0.error}`;
        }
      } else if (result.auth0?.note === "email_not_deliverable_skipped_auth0") {
        suffix = " · Email doesn't look deliverable — Auth0 skipped, local password only";
      } else if (draftEmail.trim() && !result.auth0?.configured) {
        suffix = " · Auth0 not configured — local password only";
      }
      notify(`Created ${id}${suffix}`);
      setDraftId(""); setDraftPw(""); setDraftEmail(""); setDraftAdmin(false);
      await refresh();
    } catch (e: any) {
      notify(e?.message || "Couldn't create user");
    } finally {
      setCreating(false);
    }
  };

  // Sync an existing Vulture user to Auth0 + send password reset email.
  // Creates the Auth0 user if missing, then triggers Auth0's "forgot
  // your password?" email so the user picks their own password. Safe
  // to re-run for forgotten-password resets too — each run sends a
  // fresh reset link.
  const provisionAuth0 = async (u: AdminUser) => {
    if (!u.email) { notify("Set an email first"); return; }
    const ok = await confirm({
      title: `Send Auth0 reset email to ${u.email}?`,
      message: u.email.endsWith("@kid.beemerworks.com")
        ? `That looks like a synthetic kid-device address — Auth0 can't deliver a reset link there. Skip and use the kid-device handle reset instead.`
        : `Creates the Auth0 user if needed and emails them a password-reset link. Safe to re-run anytime.`,
      confirmLabel: u.email.endsWith("@kid.beemerworks.com") ? "Send anyway" : "Send reset email",
    });
    if (!ok) return;
    try {
      const r = await api.adminProvisionAuth0(u.id);
      notify(
        r.created
          ? `Created Auth0 user + sent reset email to ${r.email}`
          : `Sent reset email to ${r.email}`,
      );
      await refresh();
    } catch (e: any) {
      notify(e?.message || "Auth0 provision failed");
    }
  };

  return (
    <>
      <div className="admin-card">
        <h3>Users</h3>
        <p className="admin-card-sub">
          Existing accounts. <em>Delete</em> removes the account and any owned data.
        </p>
        {users === null ? (
          <p className="admin-loading">Loading…</p>
        ) : users.length === 0 ? (
          <p className="admin-empty">No users — that's not great.</p>
        ) : (
          users.map((u) => {
            const emailDraft = emailDrafts[u.id] ?? u.email ?? "";
            const emailDirty = emailDraft.trim() !== (u.email || "").trim();
            const isSelf = u.id === meId;
            return (
              <div key={u.id} className="admin-user-row">
                <div>
                  <div className="admin-user-id-line">
                    <span className="admin-user-mono">{u.id}</span>
                    {u.isAdmin && <span className="admin-pill admin-pill-admin">admin</span>}
                    {u.hasTotp && <span className="admin-pill admin-pill-2fa">2fa</span>}
                    {u.forceTotp && <span className="admin-pill admin-pill-force">forced</span>}
                    {isSelf && <span className="admin-pill">you</span>}
                  </div>
                  <div className="admin-user-date">created {u.createdAt.slice(0, 10)}</div>
                </div>
                <input
                  className="input admin-user-email"
                  placeholder="email (Auth0 login)"
                  value={emailDraft}
                  onInput={(e) => editEmail(u.id, (e.currentTarget as HTMLInputElement).value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEmail(u); } }}
                />
                <div className="admin-row-actions">
                  <button
                    className="admin-mini-btn"
                    onClick={() => saveEmail(u)}
                    disabled={!emailDirty}
                    type="button"
                  >save</button>
                  <button
                    className="admin-mini-btn"
                    onClick={() => provisionAuth0(u)}
                    disabled={!u.email}
                    type="button"
                    title={u.email ? "Create/sync Auth0 user + send password reset email" : "Set an email first"}
                  >send auth0 reset</button>
                  <button
                    className="admin-mini-btn admin-mini-warn"
                    onClick={() => toggleForceTotp(u)}
                    type="button"
                  >
                    {u.forceTotp ? "unforce totp" : "force totp"}
                  </button>
                  <button
                    className="admin-mini-btn admin-mini-danger"
                    onClick={() => remove(u)}
                    disabled={isSelf}
                    type="button"
                  >delete</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="admin-card">
        <h3>Create user</h3>
        <p className="admin-card-sub">
          Provision a new account. Password must be at least 6 characters.
        </p>
        <div className="admin-form-grid">
          <div>
            <label>username</label>
            <input className="input admin-mono-input"
              value={draftId}
              onInput={(e) => setDraftId((e.currentTarget as HTMLInputElement).value)}
            />
          </div>
          <div>
            <label>password (≥ 6 chars)</label>
            <input type="password" className="input admin-mono-input"
              value={draftPw}
              onInput={(e) => setDraftPw((e.currentTarget as HTMLInputElement).value)}
            />
          </div>
          <div className="admin-form-full">
            <label>email · used for Auth0 login (auto-creates the Auth0 user + sends a password-reset email when supplied)</label>
            <input className="input admin-mono-input"
              placeholder="user@example.com"
              value={draftEmail}
              onInput={(e) => setDraftEmail((e.currentTarget as HTMLInputElement).value)}
            />
          </div>
          <div className="admin-form-full admin-form-foot">
            <label className="admin-form-checkbox">
              <input
                type="checkbox"
                checked={draftAdmin}
                onChange={(e) => setDraftAdmin((e.currentTarget as HTMLInputElement).checked)}
              />
              Admin
            </label>
            <button className="btn btn-primary" onClick={create} disabled={creating} type="button">
              <Icon name="plus" size={13} /> {creating ? "Creating…" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
